import type { Session } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { setServerOffset } from '@/lib/time'
import type { Profile } from '@/lib/db'

type AuthStatus = 'loading' | 'authed' | 'anon'

/* Only the columns the select below actually asks for. Widening this to the
   full `Profile` row would be a lie — `email`, `is_admin` and
   `stripe_customer_id` are never fetched here, and typing them as present
   invites someone to read `profile.is_admin` and get undefined. */
type AuthProfile = Pick<Profile, 'id' | 'display_name' | 'avatar_url'>

interface AuthValue {
  session: Session | null
  user: Session['user'] | null
  profile: AuthProfile | null
  status: AuthStatus
  signOut: () => Promise<unknown>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<AuthProfile | null>(null)
  const [status, setStatus] = useState<AuthStatus>('loading')

  useEffect(() => {
    let alive = true

    async function loadProfile(userId: string) {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .eq('id', userId)
        .single()
      if (alive && !error) setProfile(data)
    }

    async function syncClock() {
      const { data, error } = await supabase.rpc('server_now')
      if (alive && !error && data) setServerOffset(new Date(data))
    }

    // onAuthStateChange fires INITIAL_SESSION the moment we subscribe, so there
    // is no separate getSession() call and no sign-in flash on refresh.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!alive) return
      setSession(next)
      setStatus(next ? 'authed' : 'anon')

      // Never await a Supabase call inside this callback. The auth client holds
      // an internal lock while it runs; a nested call deadlocks token refresh
      // and the app quietly stops refreshing about an hour later.
      if (next?.user) {
        const uid = next.user.id
        queueMicrotask(() => {
          void loadProfile(uid)
          void syncClock()
        })
      } else {
        setProfile(null)
      }
    })

    return () => {
      alive = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      status,
      signOut: () => supabase.auth.signOut(),
    }),
    [session, profile, status]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
